import { useEffect, useState } from 'react';
import { groupTripApi } from '../../api/groupTripApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import {
  groupTripErrorMessage,
  normalizeActivityParticipationList,
  normalizeJourneyActivityList,
  participationStateLabel,
} from './groupTripModel';

const ACTIONS = {
  away: 'TEMPORARILY_AWAY',
  endAway: 'END_TEMPORARILY_AWAY',
  skipped: 'SKIPPED',
  attended: 'ATTENDED',
  clear: 'CLEAR',
};

export default function GroupTripActivityParticipation({ groupTripId }) {
  const { t } = useTravelTranslation();
  const [activities, setActivities] = useState([]);
  const [activityId, setActivityId] = useState('');
  const [rows, setRows] = useState([]);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!groupTripId || typeof groupTripApi.listJourneyActivities !== 'function') return undefined;
    let cancelled = false;
    groupTripApi.listJourneyActivities(groupTripId)
      .then((payload) => {
        if (cancelled) return;
        const items = normalizeJourneyActivityList(payload).items;
        setActivities(items);
        setActivityId((current) => current || items[0]?.journeyActivityId || '');
      })
      .catch(() => {
        if (!cancelled) setActivities([]);
      });
    return () => { cancelled = true; };
  }, [groupTripId]);

  useEffect(() => {
    if (!groupTripId || !activityId || typeof groupTripApi.readActivityParticipation !== 'function') return undefined;
    let cancelled = false;
    groupTripApi.readActivityParticipation(groupTripId, activityId)
      .then((payload) => {
        if (!cancelled) setRows(normalizeActivityParticipationList(payload).items);
      })
      .catch((cause) => {
        if (!cancelled) setError(groupTripErrorMessage(cause, t, 'travel.common.actionFailed'));
      });
    return () => { cancelled = true; };
  }, [groupTripId, activityId, t]);

  if (typeof groupTripApi.listJourneyActivities !== 'function') return null;

  async function apply(travelerId, action) {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await groupTripApi.updateTravelerParticipation(groupTripId, activityId, travelerId, {
        action,
        reason: reason.trim() || null,
      });
      const payload = await groupTripApi.readActivityParticipation(groupTripId, activityId);
      setRows(normalizeActivityParticipationList(payload).items);
      setReason('');
    } catch (cause) {
      setError(groupTripErrorMessage(cause, t, 'travel.common.actionFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="group-trips__panel" data-testid="activity-participation">
      <h2>{t('travel.groupTrip.participationHeading')}</h2>
      <p className="group-trips__hint">{t('travel.groupTrip.participationHint')}</p>
      {activities.length === 0 ? <p className="group-trips__hint">{t('travel.groupTrip.noJourneyActivities')}</p> : (
        <label>
          {t('travel.groupTrip.participationHeading')}
          <select
            value={activityId}
            onChange={(event) => setActivityId(event.target.value)}
            data-testid="activity-participation-select"
          >
            {activities.map((activity) => (
              <option key={activity.journeyActivityId} value={activity.journeyActivityId}>
                {activity.title || activity.journeyActivityId}
              </option>
            ))}
          </select>
        </label>
      )}
      <label>
        {t('travel.groupTrip.participationReason')}
        <input
          value={reason}
          maxLength={200}
          onChange={(event) => setReason(event.target.value)}
          data-testid="activity-participation-reason"
        />
      </label>
      {error ? <p role="alert">{error}</p> : null}
      <ul className="group-trips__list">
        {rows.map((row) => (
          <li className="group-trips__card" key={row.travelerId} data-testid={`participation-${row.travelerId}`}>
            <p>
              {row.displayName}
              {' · '}
              {row.guest ? t('travel.groupTrip.guestLabel') : t('travel.groupTrip.linkedLabel')}
              {' · '}
              {participationStateLabel(row.effectiveState, t, row.travelerStatus)}
            </p>
            {row.canUpdate && row.effectiveState !== 'NOT_JOINED_YET' ? (
              <div className="group-trips__actions" data-testid={`participation-actions-${row.travelerId}`}>
                {row.effectiveState === 'TEMPORARILY_AWAY' ? (
                  <button type="button" disabled={busy} onClick={() => apply(row.travelerId, ACTIONS.endAway)}>
                    {t('travel.groupTrip.participationEndAway')}
                  </button>
                ) : (
                  <button type="button" disabled={busy} onClick={() => apply(row.travelerId, ACTIONS.away)}>
                    {t('travel.groupTrip.participationAway')}
                  </button>
                )}
                <button type="button" disabled={busy} onClick={() => apply(row.travelerId, ACTIONS.skipped)}>
                  {t('travel.groupTrip.participationSkipped')}
                </button>
                <button type="button" disabled={busy} onClick={() => apply(row.travelerId, ACTIONS.attended)}>
                  {t('travel.groupTrip.participationAttended')}
                </button>
                {row.effectiveState !== 'EXPECTED' ? (
                  <button type="button" disabled={busy} onClick={() => apply(row.travelerId, ACTIONS.clear)}>
                    {t('travel.groupTrip.participationClear')}
                  </button>
                ) : null}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
